// src/pages/auth/Login.jsx
// Ultra-modern Login Portal (Galaxy + Aurora + Cinematic Effects)

import React, { useState, useCallback, useMemo, useEffect, useRef } from "react";
import { Container, Row, Col, Card, Form, Button, InputGroup, Badge, Spinner } from "react-bootstrap";
import { useAuth } from "../../context/AuthContext";
import { toast, ToastContainer } from "react-toastify";
import { useNavigate, useLocation } from "react-router-dom";
import { auth as fbAuth, db } from "../../services/firebase";
import { doc, getDoc, collection, query, where, getDocs } from "firebase/firestore";
import Particles from "react-tsparticles";
import { loadSlim } from "tsparticles-slim";
import logo from "../../assets/logo.png";

/* ---------------- helpers ---------------- */
const normalizeRole = (r) => {
  const v = String(r || "").toLowerCase();
  if (v === "admin") return "superadmin";
  if (v === "school_admin" || v === "school") return "schooladmin";
  return v;
};
const cssVar = (name, fallback) =>
  typeof window !== "undefined"
    ? getComputedStyle(document.documentElement).getPropertyValue(name).trim() || fallback
    : fallback;
const hexToRgba = (hex, a = 1) => {
  let c = String(hex || "").replace("#", "");
  if (c.length === 3) c = c.split("").map((x) => x + x).join("");
  if (c.length !== 6) return `rgba(0,0,0,${a})`;
  const n = parseInt(c, 16);
  const r = (n >> 16) & 255,
    g = (n >> 8) & 255,
    b = n & 255;
  return `rgba(${r}, ${g}, ${b}, ${a})`;
};

/* --- micro helpers: shake & confetti --- */
function triggerShake(el) {
  if (!el) return;
  el.classList.remove("shake");
  void el.offsetWidth;
  el.classList.add("shake");
}
function confettiBurst() {
  const c = document.createElement("canvas");
  c.style.cssText = "position:fixed;inset:0;pointer-events:none;z-index:9999";
  document.body.appendChild(c);
  const ctx = c.getContext("2d");
  let w = (c.width = window.innerWidth);
  let h = (c.height = window.innerHeight);
  const colors = ["#ffcb6b", "#6a8cff", "#8ae5ff", "#bd8bff", "#ffffff"];
  const shapes = ["square", "circle", "star"];
  const pieces = Array.from({ length: 120 }, () => ({
    x: Math.random() * w,
    y: -20 - Math.random() * h * 0.4,
    r: 4 + Math.random() * 4,
    s: 2 + Math.random() * 3,
    a: Math.random() * Math.PI,
    c: colors[(Math.random() * colors.length) | 0],
    vx: -1 + Math.random() * 2,
    shape: shapes[(Math.random() * shapes.length) | 0],
  }));
  let id = 0,
    start = performance.now();
  const draw = (t) => {
    ctx.clearRect(0, 0, w, h);
    pieces.forEach((p) => {
      p.y += p.s;
      p.x += p.vx;
      p.a += 0.1;
      ctx.save();
      ctx.translate(p.x, p.y);
      ctx.rotate(p.a);
      ctx.fillStyle = p.c;
      if (p.shape === "circle") {
        ctx.beginPath();
        ctx.arc(0, 0, p.r, 0, Math.PI * 2);
        ctx.fill();
      } else if (p.shape === "star") {
        ctx.beginPath();
        for (let i = 0; i < 5; i++) {
          ctx.lineTo(
            Math.cos(((18 + i * 72) / 180) * Math.PI) * p.r,
            Math.sin(((18 + i * 72) / 180) * Math.PI) * p.r
          );
          ctx.lineTo(
            Math.cos(((54 + i * 72) / 180) * Math.PI) * p.r * 0.5,
            Math.sin(((54 + i * 72) / 180) * Math.PI) * p.r * 0.5
          );
        }
        ctx.closePath();
        ctx.fill();
      } else ctx.fillRect(-p.r, -p.r, p.r * 2, p.r * 2);
      ctx.restore();
    });
    id = requestAnimationFrame(draw);
    if (t - start > 1600) {
      cancelAnimationFrame(id);
      c.remove();
    }
  };
  id = requestAnimationFrame(draw);
  window.addEventListener(
    "resize",
    () => {
      w = c.width = window.innerWidth;
      h = c.height = window.innerHeight;
    },
    { once: true }
  );
}

const ROLE_HOME = {
  superadmin: "/admin",
  schooladmin: "/school",
  teacher: "/teacher",
  student: "/student",
};
const ROLES = [
  {
    key: "schooladmin",
    label: "School Admin",
    icon: "bi-building",
    hint: "Manage school, fees, classes",
  },
  {
    key: "teacher",
    label: "Teacher",
    icon: "bi-person-workspace",
    hint: "Create classes & exams",
  },
  {
    key: "student",
    label: "Student",
    icon: "bi-mortarboard",
    hint: "Join classes, attempt tests",
  },
];

const Login = () => {
  const { login, user: authedUser, role: myRole, authReady, profileReady } =
    useAuth();
  const [role, setRole] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const cardRef = useRef(null);

  const readyAndAuthed = authReady && profileReady && !!authedUser;
  const home = ROLE_HOME[myRole] || "/";

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const r = (params.get("role") || "").toLowerCase();
    if (["superadmin", "schooladmin", "teacher", "student"].includes(r))
      setRole(r);
  }, [location.search]);

  const nextPath = useMemo(() => {
    const params = new URLSearchParams(location.search);
    const n = params.get("next");
    return n && n.startsWith("/") ? n : null;
  }, [location.search]);

  const brand = useMemo(() => {
    const primary = cssVar("--brand-primary", "#6a8cff");
    const accent = cssVar("--brand-accent", "#ffcb6b");
    const bgSoft = "#040814";
    const bgCard = "rgba(17,25,40,.58)";
    return { primary, accent, bgSoft, bgCard };
  }, []);

  const particlesInit = useCallback(async (engine) => {
    await loadSlim(engine);
  }, []);
  useEffect(() => {
    if (readyAndAuthed) navigate(home, { replace: true });
  }, [readyAndAuthed, home, navigate]);

  if (!authReady || !profileReady)
    return (
      <div className="d-flex align-items-center justify-content-center min-vh-100">
        <Spinner animation="border" />
        <span className="ms-2 text-muted">Preparing sign-in…</span>
      </div>
    );
  if (readyAndAuthed)
    return (
      <div className="d-flex align-items-center justify-content-center min-vh-100">
        <Spinner animation="border" />
        <span className="ms-2 text-muted">Redirecting…</span>
      </div>
    );

  /* Firestore helpers */
  const getStudentDirect = async (sid, stId) => {
    const snap = await getDoc(doc(db, "schools", sid, "students", stId));
    return snap.exists() ? { id: snap.id, data: snap.data() } : null;
  };
  const findStudentByEmail = async (sid, mail) => {
    const qs = await getDocs(
      query(
        collection(db, "schools", sid, "students"),
        where("email", "==", String(mail || "").toLowerCase())
      )
    );
    if (!qs.empty) {
      const d = qs.docs[0];
      return { id: d.id, data: d.data() };
    }
    return null;
  };
  const clearStudentLocals = () => {
    try {
      localStorage.removeItem("schoolId");
      localStorage.removeItem("studentId");
      localStorage.removeItem("studentName");
      localStorage.removeItem("studentDisplayName");
    } catch {}
  };
  const writeStudentLocals = (sid, stu, disp) => {
    try {
      localStorage.setItem("schoolId", sid);
      localStorage.setItem("studentId", stu.id);
      localStorage.setItem("studentName", stu.data?.name || "");
      localStorage.setItem(
        "studentDisplayName",
        disp || stu.data?.name || ""
      );
    } catch {}
  };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const emailNorm = email.trim().toLowerCase();
      const result = await login(emailNorm, password);
      const uid =
        result?.user?.uid ||
        result?.cred?.user?.uid ||
        fbAuth.currentUser?.uid;
      if (!uid) {
        toast.error("Sign-in succeeded, but user not found.");
        triggerShake(cardRef.current);
        return;
      }
      let profile;
      try {
        const snap = await getDoc(doc(db, "users", uid));
        if (!snap.exists()) {
          toast.error("Profile not found.");
          triggerShake(cardRef.current);
          return;
        }
        profile = snap.data() || {};
      } catch {
        toast.error("You don't have permission to load profile.");
        triggerShake(cardRef.current);
        return;
      }
      const dbRole = normalizeRole(profile.role);
      if (!ROLE_HOME[dbRole]) {
        toast.error(`Unknown role "${profile.role}"`);
        triggerShake(cardRef.current);
        return;
      }
      if (dbRole !== "student") clearStudentLocals();
      else {
        const { schoolId, studentId } = profile;
        if (!schoolId) {
          toast.error("No school linked.");
          triggerShake(cardRef.current);
          return;
        }
        let found = studentId
          ? await getStudentDirect(schoolId, studentId)
          : null;
        if (!found) found = await findStudentByEmail(schoolId, emailNorm);
        if (!found) {
          toast.error("Student record not found.");
          triggerShake(cardRef.current);
          return;
        }
        writeStudentLocals(schoolId, found, profile.displayName);
      }
      toast.success("Welcome back");
      if (!sessionStorage.getItem("lsConfettiShown")) {
        confettiBurst();
        sessionStorage.setItem("lsConfettiShown", "1");
      }
      navigate(nextPath || ROLE_HOME[dbRole], { replace: true });
    } catch (err) {
      const code = err?.code || "";
      const msg =
        code === "auth/invalid-email"
          ? "Please enter a valid email."
          : code === "auth/invalid-credential"
          ? "Invalid email or password."
          : code === "auth/user-not-found"
          ? "No user with that email."
          : code === "auth/wrong-password"
          ? "Incorrect password."
          : code === "auth/too-many-requests"
          ? "Too many attempts. Try again later."
          : "Sign-in failed. Please try again.";
      toast.error(msg);
      triggerShake(cardRef.current);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="auth-wrap min-vh-100 position-relative overflow-hidden"
      style={{ background: brand.bgSoft }}
    >
      <GalaxyBackground primary={brand.primary} />
      <AuroraRibbons primary={brand.primary} accent={brand.accent} />
      <Particles
        id="tsparticles"
        className="position-fixed top-0 start-0 w-100 h-100"
        init={particlesInit}
        options={{
          fpsLimit: 60,
          detectRetina: true,
          fullScreen: { enable: false },
          background: { color: { value: "transparent" } },
          particles: {
            number: { value: 20, density: { enable: true, area: 900 } },
            color: { value: [brand.primary, "#fff"] },
            links: { enable: false },
            move: { enable: true, speed: 0.4 },
            opacity: { value: 0.28 },
            size: { value: { min: 1, max: 1.6 } },
          },
        }}
      />
      <Container className="position-relative" style={{ zIndex: 2 }}>
        <ToastContainer theme="dark" />
        <Row className="justify-content-center py-5">
          <Col xl={5} lg={6} md={9}>
            <Tilt3D
              intensity={8}
              glare
              hoverPop={1.02}
              glareOpacity={0.14}
              radius={22}
            >
              <Card
                ref={cardRef}
                className="auth-card p-4 p-md-5 position-relative border-0 shadow-xxl neon-frame"
                style={{
                  background: brand.bgCard,
                  backdropFilter: "blur(14px)",
                  borderRadius: "1.3rem",
                  overflow: "visible",
                }}
              >
                
                <div className="mb-4 text-center logo-stack">
                  <img
                    src={logo}
                    alt="LearnSmart Logo"
                    className="brand-logo mb-3 floating"
                    style={{ height: 140 }}
                  />
                  <div className="logo-halo" />
                  <div className="logo-rays" />
                  <div className="logo-sparkle" />
                  <div className="logo-flare" />
                <h2
  className="fw-bold mb-1 gradient-animated-text"
  style={{
    fontFamily: "'Fascinate', cursive",
    fontSize: "2rem",
    letterSpacing: "1px",
    textTransform: "uppercase"
  }}
>
LearnSmart
</h2>
                  <div  className="font-monospace fw-bolder badge bg-primary">
                    Choose your role & sign in securely
                  </div>
                </div>
                <div className="role-grid mb-3 ">
                  {ROLES.map((r) => (
                    <RoleTile 
                      key={r.key}
                      active={role === r.key}
                      onClick={() => setRole(r.key)}
                      primary={brand.primary}
                      {...r}
                    />
                  ))}
                </div>
                <Form onSubmit={submit} noValidate>
                  <Form.Group className="mb-3">
                    <Form.Label className="fw-bold text-primary">Email</Form.Label>
                    <Form.Control
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="you@school.com"
                      autoComplete="username"
                      required
                      className="focus-glow stardust-input"
                    />
                  </Form.Group>
                  <Form.Group className="mb-3">
                    <Form.Label className="fw-bold text-primary">Password</Form.Label>
                    <InputGroup>
                      <Form.Control
                        type={showPw ? "text" : "password"}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder="Enter password"
                        autoComplete="current-password"
                        required
                        className="focus-glow stardust-input"
                      />
                      <Button
                        variant="outline-secondary"
                        onClick={() => setShowPw((s) => !s)}
                        type="button"
                        aria-label={showPw ? "Hide" : "Show"}
                      >
                        <i
                          className={`bi ${
                            showPw ? "bi-eye-slash" : "bi-eye"
                          }`}
                        />
                      </Button>
                    </InputGroup>
                  </Form.Group>
                  <Button
                    type="submit"
                    className="w-100 btn-lg sheen-btn warp-btn"
                    disabled={loading}
                    style={{
                      borderRadius: "0.9rem",
                      backgroundColor: brand.primary,
                      borderColor: brand.primary,
                      position: "relative",
                      overflow: "hidden",
                    }}
                  >
                    <span className="btn-sheen" />
                    {loading ? (
                      <span className="d-inline-flex align-items-center gap-2">
                        <Spinner size="sm" animation="border" /> Signing in…
                      </span>
                    ) : (
                      `Sign In${role ? ` as ${role}` : ""}`
                    )}
                  </Button>
                </Form>
              </Card>
            </Tilt3D>
          </Col>
        </Row>
      </Container>
     <style>{`
     .gradient-animated-text {
  background: linear-gradient(
    270deg,
    #ff6ec4,
    #7873f5,
    #42e695,
    #ffcc70,
    #ff6ec4
  );
  background-size: 800% 800%;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  animation: gradientFlow 10s ease infinite;
}

@keyframes gradientFlow {
  0% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
}

  .gradient-text {
    background: linear-gradient(90deg, var(--brand-primary, #6a8cff), var(--brand-accent, #ffcb6b));
    -webkit-background-clip: text;
    background-clip: text;
    color: transparent;
  }
  .floating { animation: floatY 6s ease-in-out infinite; }
  @keyframes floatY { 0%{transform:translateY(0)} 50%{transform:translateY(-8px)} 100%{transform:translateY(0)} }

  /* Neon frame around card */


  /* Logo effects */
  .logo-stack { position:relative; display:grid; place-items:center; }
  .logo-halo {
    position:absolute; width:260px; height:260px; border-radius:50%;
    background: radial-gradient(circle, rgba(255,255,255,.15) 0%, transparent 70%);
    filter: blur(20px);
    animation: haloPulse 5s ease-in-out infinite;
  }
  @keyframes haloPulse { 0%,100%{transform:scale(1);opacity:.3} 50%{transform:scale(1.1);opacity:.6} }
  
  @keyframes raysSpin { to{ transform: rotate(360deg);} }
  .logo-sparkle {
    position:absolute; width:100%; height:100%; pointer-events:none;
    background-image: radial-gradient(circle, #fff, transparent 60%);
    background-repeat: no-repeat;
    animation: sparkleFlash 3.5s infinite steps(1);
  }
  @keyframes sparkleFlash {
    0%, 90%, 100% { opacity:0; background-position: -9999px -9999px;}
    40% { opacity:.8; background-size:6px 6px; background-position: 70% 30%;}
    60% { opacity:.8; background-size:5px 5px; background-position: 30% 70%;}
  }
  .logo-flare {
    position:absolute; width:400px; height:60px; top:50%; left:50%;
    transform:translate(-50%,-50%) rotate(25deg);
    background: linear-gradient(90deg, transparent, rgba(255,255,255,.4), transparent);
    opacity:.3; filter: blur(20px);
    animation: flareSweep 8s linear infinite;
  }
  @keyframes flareSweep {
    0%{transform:translate(-150%,-50%) rotate(25deg);}
    100%{transform:translate(150%,-50%) rotate(25deg);}
  }

  /* Button warp ripple */
  .sheen-btn .btn-sheen {
    pointer-events:none; position:absolute; top:0; left:-150%; width:50%; height:100%;
    background: linear-gradient(90deg, transparent, rgba(255,255,255,0.5), transparent);
    transform: skewX(-20deg); animation: btnSheen 2.8s ease-in-out infinite;
  }
  @keyframes btnSheen { 0%{left:-150%} 60%{left:150%} 100%{left:150%} }
  .warp-btn:active { animation: warpPulse 0.45s ease-out; }
  @keyframes warpPulse {
    0%{ transform:scale(1); }
    40%{ transform:scale(1.06); box-shadow:0 0 30px rgba(255,255,255,.4); }
    100%{ transform:scale(1); }
  }

  /* Stardust input glow */
  .stardust-input { position:relative; z-index:1; }
  .stardust-input:focus { box-shadow:0 0 0 .25rem rgba(106,140,255,0.35) !important; border-color:rgba(106,140,255,0.6) !important; }
  .stardust-input:focus::after {
    content:""; position:absolute; inset:0; pointer-events:none;
    background: radial-gradient(circle, rgba(255,255,255,.1), transparent 60%);
    animation: stardustGlow 1.4s infinite alternate;
  }
  @keyframes stardustGlow { from{opacity:0} to{opacity:.6} }

  /* Role grid */
  .role-grid{ display:grid; grid-template-columns: repeat(3, 1fr); gap:10px; }
  @media (max-width:100%){ .role-grid{ grid-template-columns:repeat(2,1fr);} }
  @media (max-width:100%){ .role-grid{ grid-template-columns:1fr;} }

  /* Role tile */
  .rt-card{ position:relative; align-items:center; border:1px solid rgba(108,117,125,0.22);
    background:rgba(11,18,32,0.55); border-radius:18px; padding:14px; min-height:138px;
    transition:border-color .18s, box-shadow .18s, transform .12s; overflow:hidden; }
  .rt-card:hover{ transform:translateY(-2px); box-shadow:0 16px 36px rgba(106,140,255,0.22); }
  .rt-card.active{ border-color:rgba(106,140,255,0.7);
    box-shadow:0 20px 48px rgba(106,140,255,0.28), inset 0 0 28px rgba(106,140,255,.12); }
  .rt-title{ margin-top:10px; font-weight:700; font-size:15px; color:#f0f5ff; text-shadow:0 0 6px rgba(106,140,255,0.25); }
  .rt-hint{ font-size:.9rem; color:rgba(226,232,246,.9); margin-top:auto; }

  /* Shake animation on error */
  .shake { animation: shakeX .45s cubic-bezier(.36,.07,.19,.97) both; }
  @keyframes shakeX {
    10%,90%{ transform: translateX(-1px);}
    20%,80%{ transform: translateX(2px);}
    30%,50%,70%{ transform: translateX(-4px);}
    40%,60%{ transform: translateX(4px);}
  }
    .fascinate-regular {
  font-family: "Fascinate", system-ui;
  font-weight: 400;
  font-style: normal;
}

    @import url('https://fonts.googleapis.com/css2?family=Fascinate&family=Poppins:ital,wght@0,100..900;1,100..900&display=swap');

`}</style>

    </div>
  );
};

export default Login;
/* ---------------- Galaxy Background (final with extras) ---------------- */
function GalaxyBackground({ primary }) {
  const canvasRef = useRef(null);

  useEffect(() => {
    const c = canvasRef.current;
    if (!c) return;
    const ctx = c.getContext("2d", { alpha: true });

    let w = (c.width = window.innerWidth);
    let h = (c.height = window.innerHeight);
    const rm = window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches;

    /* ---------------- Mouse State ---------------- */
    const mouse = { x: w * 0.5, y: h * 0.5, tx: w * 0.5, ty: h * 0.5, vx: 0, vy: 0 };
    const onMove = (e) => {
      const px = e.touches ? e.touches[0].clientX : e.clientX;
      const py = e.touches ? e.touches[0].clientY : e.clientY;
      mouse.tx = px; mouse.ty = py;
    };
    window.addEventListener("pointermove", onMove, { passive: true });
    window.addEventListener("touchmove", onMove, { passive: true });

    /* ---------------- Helpers ---------------- */
    const R = (min, max) => min + Math.random() * (max - min);
    const clamp = (v, a, b) => Math.max(a, Math.min(b, v));

    /* ---------------- Stars ---------------- */
    const countBase = (w * h) / 9500;
    const stars = [];
    const colors = ["#ffffff", "#ffd27d", "#ff9b7d", "#a7d0ff", "#d0a7ff"];

    for (let i = 0; i < countBase; i++) {
      stars.push({
        x: Math.random() * w,
        y: Math.random() * h,
        r: R(0.6, 3.5),
        s: R(0.02, 0.1),
        tb: R(0.6, 1.2),
        c: colors[(Math.random() * colors.length) | 0],
      });
    }

    // Giant halo stars
    const giants = Array.from({ length: 6 }, () => ({
      x: Math.random() * w,
      y: Math.random() * h,
      r: R(2.8, 4.2),
      halo: R(120, 200),
      hue: R(180, 280),
    }));

    /* ---------------- Stardust Tail ---------------- */
    const tail = [];
    function pushTail(x, y, vx, vy) {
      if (rm) return;
      for (let i = 0; i < 3; i++) {
        tail.push({
          x, y,
          vx: vx * R(0.1, 0.3) + R(-0.2, 0.2),
          vy: vy * R(0.1, 0.3) + R(-0.2, 0.2),
          life: 1,
          r: R(0.8, 1.6),
        });
      }
      if (tail.length > 250) tail.splice(0, tail.length - 250);
    }

    /* ---------------- Meteors ---------------- */
    let meteor = { life: 0, x: 0, y: 0, vx: 0, vy: 0 };

    /* ---------------- Click Bursts ---------------- */
    const bursts = [];
    const onClick = (e) => {
      const x = e.clientX, y = e.clientY;
      for (let i = 0; i < 35; i++) {
        bursts.push({
          x, y, r: R(2, 4),
          a: R(0, Math.PI * 2),
          s: R(1.5, 3),
          life: 1,
          c: colors[(Math.random() * colors.length) | 0],
        });
      }
    };
    window.addEventListener("click", onClick);

    /* ---------------- Animation ---------------- */
    let raf = 0;
    const loop = (t) => {
      mouse.vx = mouse.tx - mouse.x;
      mouse.vy = mouse.ty - mouse.y;
      mouse.x += mouse.vx * 0.18;
      mouse.y += mouse.vy * 0.18;

      // background
      const g = ctx.createLinearGradient(0, 0, 0, h);
      g.addColorStop(0, "rgba(4,8,20,1)");
      g.addColorStop(1, "rgba(2,6,14,1)");
      ctx.fillStyle = g; ctx.fillRect(0, 0, w, h);

      // nebula
      drawNebula(w * 0.2, h * 0.7, 520, hexToRgba(primary, .18), 32);
      drawNebula(w * 0.8, h * 0.25, 480, "rgba(120,100,255,.12)", 28);

      // stars
      drawStars(stars, t);
      drawGiants(giants, t);

      // constellation lines
      drawConstellations(stars);

      // cursor glow only (no beam)
      if (!rm) {
        const glow = ctx.createRadialGradient(mouse.x, mouse.y, 0, mouse.x, mouse.y, 160);
        glow.addColorStop(0, "rgba(255,255,255,0.12)");
        glow.addColorStop(1, "rgba(255,255,255,0)");
        ctx.fillStyle = glow; ctx.fillRect(0, 0, w, h);
      }

      // stardust trail
      if (!rm && (Math.abs(mouse.vx) + Math.abs(mouse.vy)) > 0.5)
        pushTail(mouse.x, mouse.y, mouse.vx * 0.02, mouse.vy * 0.02);
      for (let i = tail.length - 1; i >= 0; i--) {
        const p = tail[i];
        p.x += p.vx; p.y += p.vy; p.life -= 0.02;
        if (p.life <= 0) { tail.splice(i, 1); continue; }
        ctx.beginPath(); ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(255,255,255,${0.7 * p.life})`;
        ctx.fill();
      }

      // meteors
      if (!rm && meteor.life <= 0 && Math.random() < 0.003)
        meteor = { x: Math.random() * w, y: -80, vx: -R(3, 5), vy: R(2, 4), life: 1 };
      if (meteor.life > 0) {
        meteor.x += meteor.vx; meteor.y += meteor.vy; meteor.life -= 0.006;
        ctx.save(); ctx.globalCompositeOperation = "screen";
        const trail = ctx.createLinearGradient(meteor.x, meteor.y, meteor.x - meteor.vx * 30, meteor.y - meteor.vy * 30);
        trail.addColorStop(0, "rgba(255,255,255,.9)"); trail.addColorStop(1, "rgba(255,255,255,0)");
        ctx.strokeStyle = trail; ctx.lineWidth = 2;
        ctx.beginPath(); ctx.moveTo(meteor.x, meteor.y);
        ctx.lineTo(meteor.x - meteor.vx * 30, meteor.y - meteor.vy * 30);
        ctx.stroke(); ctx.restore();
      }

      // bursts
      for (let i = bursts.length - 1; i >= 0; i--) {
        const b = bursts[i];
        b.x += Math.cos(b.a) * b.s; b.y += Math.sin(b.a) * b.s; b.life -= 0.02;
        if (b.life <= 0) { bursts.splice(i, 1); continue; }
        ctx.beginPath(); ctx.arc(b.x, b.y, b.r, 0, Math.PI * 2);
        ctx.fillStyle = `${b.c.replace(")", `,${b.life})`).replace("rgb", "rgba")}`;
        ctx.fill();
      }

      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);

    /* ---------------- Drawing Helpers ---------------- */
    function drawNebula(cx, cy, r, color, blurPx) {
      const grad = ctx.createRadialGradient(cx, cy, 0, cx, cy, r);
      grad.addColorStop(0, color); grad.addColorStop(1, "rgba(0,0,0,0)");
      ctx.fillStyle = grad; ctx.filter = `blur(${blurPx}px)`; ctx.fillRect(0, 0, w, h); ctx.filter = "none";
    }

    function drawStars(arr, t) {
      for (const s of arr) {
        if (!rm) s.x -= s.s;
        if (s.x < -2) s.x = w + 2;
        const tw = 0.55 + 0.45 * Math.sin(t * 0.001 * s.s * 60 + s.x * 0.01 + s.y * 0.008);
        ctx.beginPath(); ctx.arc(s.x, s.y, s.r, 0, Math.PI * 2);
        ctx.fillStyle = hexToRgba(s.c, s.tb * tw); ctx.fill();
      }
    }

    function drawGiants(arr) {
      for (const g of arr) {
        const halo = ctx.createRadialGradient(g.x, g.y, 0, g.x, g.y, g.halo);
        halo.addColorStop(0, `hsla(${g.hue},90%,75%,.14)`); halo.addColorStop(1, "rgba(0,0,0,0)");
        ctx.fillStyle = halo; ctx.filter = "blur(24px)"; ctx.fillRect(0, 0, w, h); ctx.filter = "none";
        ctx.beginPath(); ctx.arc(g.x, g.y, g.r, 0, Math.PI * 2);
        ctx.fillStyle = "rgba(255,255,255,.95)"; ctx.fill();
      }
    }

    function drawConstellations(arr) {
      ctx.save();
      ctx.strokeStyle = "rgba(255,255,255,.08)";
      ctx.lineWidth = 0.6;
      for (let i = 0; i < arr.length; i++) {
        for (let j = i + 1; j < arr.length; j++) {
          const a = arr[i], b = arr[j];
          const dist = (a.x - b.x) ** 2 + (a.y - b.y) ** 2;
          if (dist < 180 * 180) {
            ctx.beginPath(); ctx.moveTo(a.x, a.y); ctx.lineTo(b.x, b.y); ctx.stroke();
          }
        }
      }
      ctx.restore();
    }

    /* ---------------- Cleanup ---------------- */
    const onResize = () => { w = c.width = window.innerWidth; h = c.height = window.innerHeight; };
    window.addEventListener("resize", onResize);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", onResize);
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("touchmove", onMove);
      window.removeEventListener("click", onClick);
    };
  }, [primary]);

  return <canvas ref={canvasRef} className="position-fixed top-0 start-0 w-100 h-100" style={{ zIndex: 0, pointerEvents: "none" }} />;
}





/* ---------------- Aurora Ribbons ---------------- */
function AuroraRibbons({ primary, accent }) {
  const rootRef = useRef(null);

  useEffect(() => {
    const el = rootRef.current;
    if (!el) return;

    const prefersReduced =
      window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches;

    let raf = 0;
    let mx = 0, my = 0;   // smoothed transform
    let tx = 0, ty = 0;   // target transform

    const onMove = (e) => {
      const vw = window.innerWidth || 1;
      const vh = window.innerHeight || 1;
      // Map cursor to a small parallax shift
      tx = ((e.clientX / vw) - 0.5) * 16; // px
      ty = ((e.clientY / vh) - 0.5) * 12; // px
    };

    const step = () => {
      // ease towards target
      mx += (tx - mx) * 0.08;
      my += (ty - my) * 0.08;
      el.style.transform = `translate3d(${mx}px, ${my}px, 0)`;
      raf = requestAnimationFrame(step);
    };

    if (!prefersReduced) {
      window.addEventListener("pointermove", onMove, { passive: true });
      raf = requestAnimationFrame(step);
    }

    return () => {
      if (!prefersReduced) {
        cancelAnimationFrame(raf);
        window.removeEventListener("pointermove", onMove);
      }
    };
  }, []);

  return (
    <div
      ref={rootRef}
      aria-hidden
      className="position-fixed top-0 start-0 w-100 h-100"
      style={{ zIndex: 1, pointerEvents: "none", overflow: "hidden", mixBlendMode: "screen" }}
    >
      {/* Upper ribbons */}
      <div
        style={{
          position: "absolute",
          width: "140%",
          height: "60%",
          left: "-20%",
          top: "-10%",
          background: `radial-gradient(60% 120% at 20% 50%, ${hexToRgba(primary, .28)} 0%, transparent 60%),
                       radial-gradient(80% 120% at 80% 50%, ${hexToRgba(accent, .22)} 0%, transparent 65%)`,
          filter: "blur(40px)",
          animation: "aurora1 22s ease-in-out infinite alternate",
        }}
      />
      {/* Lower ribbons */}
      <div
        style={{
          position: "absolute",
          width: "140%",
          height: "60%",
          left: "-20%",
          bottom: "-10%",
          background: `radial-gradient(60% 120% at 70% 50%, ${hexToRgba("#8ae5ff", .18)} 0%, transparent 60%),
                       radial-gradient(80% 120% at 30% 50%, ${hexToRgba("#bd8bff", .18)} 0%, transparent 65%)`,
          filter: "blur(46px)",
          animation: "aurora2 26s ease-in-out infinite alternate",
        }}
      />
      <style>{`
        @keyframes aurora1 {
          0%   { transform: translateY(-4%) rotate(0deg); }
          100% { transform: translateY(6%) rotate(8deg); }
        }
        @keyframes aurora2 {
          0%   { transform: translateY(6%) rotate(-6deg); }
          100% { transform: translateY(-4%) rotate(0deg); }
        }
      `}</style>
    </div>
  );
}


/* ---------------- RectOrbitComet ---------------- */



/* ---------------- RoleTile ---------------- */
function RoleTile({ key: _k, label, icon, hint, active, onClick, primary }) {
  return (
    <div className="tilt-container">
      <button
        type="button"
        className={`rt-card ${active ? "active" : ""}`}
        onClick={onClick}
        aria-label={`Select ${label}`}
        onMouseMove={(e) => {
          const b = e.currentTarget.getBoundingClientRect();
          const x = ((e.clientX - b.left) / b.width) * 100;
          const y = ((e.clientY - b.top) / b.height) * 100;
          e.currentTarget.style.setProperty("--mx", `${x}%`);
          e.currentTarget.style.setProperty("--my", `${y}%`);
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.setProperty("--mx", `50%`);
          e.currentTarget.style.setProperty("--my", `50%`);
        }}
      >
        <div className="rt-head">
          <div className="rt-icon">
            <i className={`bi ${icon}`} />
          </div>
          {active && <CornerSelected primary={primary} />}
        </div>
        <div className="rt-title" title={label}>{label}</div>
        {hint && <div className="rt-hint">{hint}</div>}
        <span className="rt-sheen" />
        {/* hover spotlight */}
        <span
          aria-hidden
          style={{
            position: "absolute",
            inset: 0,
            borderRadius: 18,
            pointerEvents: "none",
            background: `radial-gradient(220px 140px at var(--mx,50%) var(--my,50%), ${hexToRgba(primary, .12)}, transparent 60%)`,
          }}
        />
      </button>
    </div>
  );
}

/* selected corner badge */
function CornerSelected({ primary }) {
  return (
    <span
      style={{
        marginLeft: "auto",
        width: 26,
        height: 26,
        borderRadius: 8,
        display: "grid",
        placeItems: "center",
        background: hexToRgba(primary, .15),
        border: `1px solid ${hexToRgba(primary, .5)}`,
        color: hexToRgba(primary, .95),
        boxShadow: `0 0 12px ${hexToRgba(primary, .4)} inset, 0 0 18px ${hexToRgba(primary, .25)}`,
      }}
    >
      <i className="bi bi-check2" />
    </span>
  );
}
/* ---------------- Tilt3D ---------------- */
function Tilt3D({
  children,
  intensity = 10,
  hoverPop = 1.015,
  glare = false,
  glareOpacity = 0.12,
  radius = 14,
}) {
  const ref = useRef(null);
  const motionReduced =
    typeof window !== "undefined" &&
    window.matchMedia &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  useEffect(() => {
    const el = ref.current;
    if (!el || motionReduced) return;

    let raf = 0;
    let entered = false;

    const setTransform = (dx, dy) => {
      const rotX = (dy * intensity).toFixed(2);
      const rotY = (-dx * intensity).toFixed(2);
      el.style.transform = `perspective(900px) rotateX(${rotX}deg) rotateY(${rotY}deg) scale(${
        entered ? hoverPop : 1
      })`;

      if (glare) {
        const g = el.querySelector(".tilt-glare");
        if (g) {
          const angle = Math.atan2(dy, dx) * (180 / Math.PI) + 180;
          const opa =
            Math.min(Math.max(Math.hypot(dx, dy) * 1.2, 0), 1) * glareOpacity;
          g.style.background = `conic-gradient(from ${angle}deg, ${hexToRgba(
            "#fff",
            opa
          )} 0deg, transparent 120deg)`;
        }
      }
    };

    const onEnter = () => {
      entered = true;
      el.style.transition = "transform .14s ease";
    };

    const onLeave = () => {
      entered = false;
      el.style.transition = "transform .25s ease";
      el.style.transform =
        "perspective(900px) rotateX(0deg) rotateY(0deg) scale(1)";
      const g = el.querySelector(".tilt-glare");
      if (g) g.style.background = "transparent";
    };

    const onMove = (e) => {
      const r = el.getBoundingClientRect();
      const cx = r.left + r.width / 2;
      const cy = r.top + r.height / 2;
      const dx = (e.clientX - cx) / (r.width / 2);
      const dy = (e.clientY - cy) / (r.height / 2);
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => setTransform(dx, dy));
    };

    el.addEventListener("pointerenter", onEnter);
    el.addEventListener("pointerleave", onLeave);
    el.addEventListener("pointermove", onMove);

    return () => {
      el.removeEventListener("pointerenter", onEnter);
      el.removeEventListener("pointerleave", onLeave);
      el.removeEventListener("pointermove", onMove);
      cancelAnimationFrame(raf);
    };
  }, [intensity, hoverPop, glare, glareOpacity, motionReduced]);

  return (
    <div
      ref={ref}
      style={{
        transformStyle: "preserve-3d",
        borderRadius: radius,
        willChange: "transform",
        position: "relative",
      }}
      className="tilt3d-wrap"
    >
      {glare && (
        <div
          className="tilt-glare"
          style={{
            position: "absolute",
            inset: 0,
            borderRadius: radius,
            pointerEvents: "none",
            mixBlendMode: "soft-light",
            zIndex: 5,
            background: "transparent",
          }}
        />
      )}
      {children}
    </div>
  );
}

